import { useField } from "formik";
import { useCallback, useEffect, useRef } from "react";
import ReactQuill from "react-quill";

import SellRoundedIcon from "@mui/icons-material/SellRounded";
import { Box, Chip, TextField, Typography } from "@mui/material";

import { CampaignAnalyticsService } from "@analytics/index";

import EmailLayoutStyles from "@pages/CreateCampaign/components/EmailLayoutStyles";

import theme from "@theme/index";

import "@utilities/CustomClipboard";

import ResourceLink from "../../ResourceLink";

import "./richTextEditorStyles.css";
import "react-quill/dist/quill.snow.css";

// Custom label for the Chip component
const CustomChipLabel = ({ tag }: { tag: string }) => (
  <Typography color="text.primary" component="div" variant="body2">
    <span style={{ fontWeight: "bold" }}>{tag}</span>
  </Typography>
);

interface ITagInsertingEmailRichTextEditorProperties {
  handleOpenTutorialModal: () => void;
}

/**
 * A specialized rich text editor that allows the user to insert predefined tags.
 * @param handleOpenTutorialModal - The function to call when the tutorial modal should be opened.
 */
export default function TagInsertingEmailRichTextEditor({
  handleOpenTutorialModal,
}: ITagInsertingEmailRichTextEditorProperties) {
  const [emailTemplateField, , emailTemplateHelpers] = useField<string>("emailTemplate");
  const quillReference = useRef<null | ReactQuill>(null);
  const [emailSubjectField] = useField<string>("emailSubject");

  const tags = [
    "[RECIPIENT_NAME]",
    "[YOUR_NAME]",
    "[ORGANIZATION_NAME]",
    "[COMPANY_NAME]",
    "[TECH_SUMMARY]",
    "[EMAIL_SIGNATURE]",
  ];

  // Store the last known cursor position
  const lastKnownPosition = useRef<number>(0);

  // Update the last known position when the selection changes
  useEffect(() => {
    const quill = quillReference.current?.getEditor();
    if (!quill) return;

    const handleSelection = (range: any) => {
      if (range) {
        lastKnownPosition.current = range.index;
      }
    };

    quill.on("selection-change", handleSelection);

    return () => {
      quill.off("selection-change", handleSelection);
    };
  }, []);

  const insertTag = useCallback((value: string) => {
    const quill = quillReference.current?.getEditor();
    if (!quill) return;

    // Get the current selection, fall back to last known position, or default to end
    let insertPosition: number;
    const currentSelection = quill.getSelection();

    if (currentSelection && typeof currentSelection.index === "number") {
      insertPosition = currentSelection.index;
    } else if (lastKnownPosition.current === undefined) {
      // If all else fails, insert at the end
      insertPosition = quill.getLength() - 1;
    } else {
      insertPosition = lastKnownPosition.current;
    }

    // Ensure we have a valid position
    insertPosition = Math.max(0, Math.min(insertPosition, quill.getLength() - 1));

    // Insert the tag
    quill.insertText(insertPosition, value);
    quill.formatText(insertPosition, value.length, "tag", true);

    // Update selection and focus
    quill.setSelection(insertPosition + value.length, 0);
    lastKnownPosition.current = insertPosition + value.length;

    // Track the analytics
    CampaignAnalyticsService.addEmailTag(value);

    // Ensure the editor gets focus
    setTimeout(() => {
      quillReference.current?.focus();
    }, 0);
  }, []);

  return (
    <Box
      sx={{
        ...EmailLayoutStyles.root,
      }}
    >
      <Box
        sx={{
          ...EmailLayoutStyles.drawerRoot,
          maxWidth: { md: "420px", sm: "300px" },
        }}
      >
        <Box
          sx={{
            ...EmailLayoutStyles.drawerHeading,
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ width: "100%" }}>
            <Box
              sx={{
                alignItems: "center",
                display: "flex",
                flexDirection: "row",
                gap: 1.5,
              }}
            >
              <Typography component="div" variant="h6">
                Compose Email Template
              </Typography>
              <ResourceLink customClickHandler={handleOpenTutorialModal} workflow="campaigns compose" />
            </Box>
            <Typography sx={{ mt: 0.75 }} variant="body2">
              Use tags to auto-fill details in emails for a personal touch:
            </Typography>
          </Box>
        </Box>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            gap: 1,
            p: 3,
          }}
        >
          {tags.map((tag) => (
            <Chip
              color="secondary"
              icon={<SellRoundedIcon fontSize="small" />}
              key={tag}
              label={<CustomChipLabel tag={tag} />}
              onClick={() => insertTag(tag)}
              sx={{
                ":hover": {
                  backgroundColor: `${theme.palette.action.hover} !important`,
                  borderColor: `${theme.palette.primary.dark} !important`,
                  color: theme.palette.primary.dark,
                },
                px: 0.5,
              }}
              variant="outlined"
            />
          ))}
        </Box>
      </Box>
      <Box sx={{ ...EmailLayoutStyles.contentRoot, gap: 0 }}>
        <TextField
          color="secondary"
          label="Subject"
          name={emailSubjectField.name}
          onBlur={emailSubjectField.onBlur}
          onChange={emailSubjectField.onChange}
          size="small"
          sx={{
            maxWidth: theme.breakpoints.values.sm,
            mb: 2,
            width: "100%",
          }}
          value={emailSubjectField.value}
          variant="outlined"
        />
        <ReactQuill
          className="dynamicHeightEditor customQuillEditor removeBorder"
          onChange={async (_, __, ___, editor) => {
            void emailTemplateHelpers.setValue(editor.getHTML());
          }}
          ref={quillReference}
          style={{
            border: "none",
            flexGrow: 2,
          }}
          theme="snow"
          value={emailTemplateField.value}
        />
      </Box>
    </Box>
  );
}
